import React, { useState } from 'react';
import { Modal, Button, Accordion } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { DirectorReportView } from './creditDirectorReportsTypes';
import { customAxiosInstanceMutator } from '../../../services/custom-axios-instance';
import {
    getStatusBadgeClass,
    renderArrayTable,
    renderObjectTable,
    toRecord
} from './creditDirectorReportsRenderers';

type ConsumerAccountStatusFilter = 'all' | 'active' | 'paid-up' | 'closed' | 'in-arrears';

const consumerAccountStatusFilterOptions: Array<{ value: ConsumerAccountStatusFilter; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'paid-up', label: 'Paid Up' },
    { value: 'closed', label: 'Closed' },
    { value: 'in-arrears', label: 'In Arrears' }
];

const toCanonicalStatus = (value: unknown): string => {
    if (typeof value !== 'string') {
        return '';
    }

    return value.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
};

interface CreditDirectorReportsModalProps {
    show: boolean;
    onHide: () => void;
    reports: DirectorReportView[];
    selectedIndex: number;
    onSelectIndex: (index: number) => void;
    aggregateScore?: number | null;
    processedCount: number;
    successCount: number;
    failedCount: number;
    showDownloadControl?: boolean;
    downloadEnabled?: boolean;
    downloadUnavailableMessage?: string;
}

const CreditDirectorReportsModal = ({
    show,
    onHide,
    reports,
    selectedIndex,
    onSelectIndex,
    aggregateScore,
    processedCount,
    successCount,
    failedCount,
    showDownloadControl = false,
    downloadEnabled = false,
    downloadUnavailableMessage = 'Download unavailable for this report.'
}: CreditDirectorReportsModalProps) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [consumerAccountStatusFilter, setConsumerAccountStatusFilter] = useState<ConsumerAccountStatusFilter>('all');
    const activeReport = reports[selectedIndex] ?? reports[0];
    const activeAttributes = toRecord(activeReport?.attributes);
    const activeXdsReport = toRecord(activeReport?.xdsReport);
    const activeScoringEngineResult = toRecord(activeReport?.scoringEngineResult);
    const accountTypeLegendSource = activeXdsReport?.accountTypeLegend;
    const accountTypeLegend = Array.isArray(accountTypeLegendSource)
        ? accountTypeLegendSource
        : [];
    const accountTypeMap = accountTypeLegend.reduce<Record<string, string>>((acc, item) => {
        const record = toRecord(item);
        const code = typeof record?.accountTypeCode === 'string' ? record.accountTypeCode.trim() : '';
        const description = typeof record?.accountTypeDesc === 'string' ? record.accountTypeDesc.trim() : '';

        if (code && description) {
            acc[code] = description;
        }
        return acc;
    }, {});
    const accountStatusSource = activeXdsReport?.consumerAccountStatus;
    const accountStatusWithLegend = Array.isArray(accountStatusSource)
        ? accountStatusSource.map((item) => {
            const record = toRecord(item);
            if (!record) {
                return item;
            }

            const { displayText, ...accountStatusFields } = record;

            const accountType = typeof accountStatusFields.accountType === 'string' ? accountStatusFields.accountType.trim() : '';
            const accountTypeDesc = accountType ? accountTypeMap[accountType] : undefined;

            return accountTypeDesc
                ? { ...accountStatusFields, accountTypeDescription: accountTypeDesc }
                : accountStatusFields;
        })
        : [];
    const filteredAccountStatusWithLegend = accountStatusWithLegend.filter((item) => {
        if (consumerAccountStatusFilter === 'all') {
            return true;
        }

        const record = toRecord(item);
        const status = toCanonicalStatus(record?.statusCodeDesc);

        if (consumerAccountStatusFilter === 'active') {
            return status === 'active';
        }

        if (consumerAccountStatusFilter === 'paid-up') {
            return status === 'paidup';
        }

        if (consumerAccountStatusFilter === 'closed') {
            return status === 'closed';
        }

        return status === 'inarrears';
    });
    const monthlyPaymentHeaderRecord = toRecord(activeXdsReport?.consumer24MonthlyPaymentHeader);
    const monthlyPaymentKeys = Array.from({ length: 24 }, (_, index) => {
        const monthNumber = index + 1;
        return `m${monthNumber.toString().padStart(2, '0')}`;
    });
    const monthLabelByKey = monthlyPaymentKeys.reduce<Record<string, string>>((acc, key) => {
        const monthLabel = monthlyPaymentHeaderRecord?.[key];
        const label = typeof monthLabel === 'string' && monthLabel.trim().length > 0
            ? monthLabel.trim()
            : key.toUpperCase();

        acc[key] = label;
        return acc;
    }, {});
    const monthlyPaymentRowsSource = activeXdsReport?.consumer24MonthlyPayment;
    const consumer24MonthlyPaymentWithMonthHeaders = Array.isArray(monthlyPaymentRowsSource)
        ? monthlyPaymentRowsSource.map((item) => {
            const record = toRecord(item);
            if (!record) {
                return item;
            }

            const transformed: Record<string, unknown> = {
                accountOpenedDate: record.accountOpenedDate,
                subscriberName: record.subscriberName,
                accountNo: record.accountNo,
                subAccountNo: record.subAccountNo
            };

            monthlyPaymentKeys.forEach((key) => {
                const label = monthLabelByKey[key];
                const safeLabel = Object.prototype.hasOwnProperty.call(transformed, label)
                    ? `${label} (${key.toUpperCase()})`
                    : label;
                transformed[safeLabel] = record[key];
            });

            return transformed;
        })
        : [];
    const xdsSections = activeXdsReport
        ? [
            { key: 'consumer-detail', title: 'Consumer Detail', content: renderObjectTable('Consumer Detail', activeXdsReport.consumerDetail) },
            {
                key: 'consumer-affordability',
                title: 'Consumer Affordability',
                content: renderObjectTable('Consumer Affordability', activeXdsReport.consumerAffordability)
            },
            {
                key: 'payment-gap',
                title: 'Payment Gap',
                content: renderObjectTable('Payment Gap', activeXdsReport.paymentGap)
            },
            {
                key: 'consumer-cpanlr-debt-summary',
                title: 'Consumer CPANLR Debt Summary',
                content: renderObjectTable(
                    'Consumer CPANLR Debt Summary',
                    activeXdsReport.consumerCpanlrDebtSummary ?? activeXdsReport.consumerCPANLRDebtSummary
                )
            },
            {
                key: 'consumer-account-status',
                title: 'Consumer Account Status',
                content: (
                    <>
                        <div className="row g-2 mb-3">
                            <div className="col-12 col-md-4">
                                <label htmlFor="consumer-account-status-filter" className="form-label fw-semibold mb-1">
                                    Account Status Filter
                                </label>
                                <select
                                    id="consumer-account-status-filter"
                                    className="form-select form-select-sm"
                                    value={consumerAccountStatusFilter}
                                    onChange={(event) =>
                                        setConsumerAccountStatusFilter(event.target.value as ConsumerAccountStatusFilter)
                                    }
                                >
                                    {consumerAccountStatusFilterOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {renderArrayTable('Consumer Account Status', filteredAccountStatusWithLegend)}
                    </>
                )
            },
            {
                key: 'consumer-24-monthly-payment',
                title: 'Consumer 24 Monthly Payment',
                content: renderArrayTable('Consumer 24 Monthly Payment', consumer24MonthlyPaymentWithMonthHeaders)
            },
            {
                key: 'consumer-definition',
                title: 'Consumer Definition',
                content: renderArrayTable('Consumer Definition', activeXdsReport.consumerDefinition)
            },
            {
                key: 'consumer-property-information',
                title: 'Consumer Property Information',
                content: renderArrayTable('Consumer Property Information', activeXdsReport.consumerPropertyInformation)
            },
            { key: 'consumer-scoring', title: 'Consumer Scoring', content: renderObjectTable('Consumer Scoring', activeXdsReport.consumerScoring) },
            { key: 'report-information', title: 'Report Information', content: renderObjectTable('Report Information', activeXdsReport.reportInformation) },
            {
                key: 'fraud-indicators-summary',
                title: 'Fraud Indicators Summary',
                content: renderObjectTable('Fraud Indicators Summary', activeXdsReport.consumerFraudIndicatorsSummary)
            },
            {
                key: 'subscriber-input-details',
                title: 'Subscriber Input Details',
                content: renderObjectTable('Subscriber Input Details', activeXdsReport.subscriberInputDetails)
            },
            { key: 'aka-names', title: 'AKA Names', content: renderArrayTable('AKA Names', activeXdsReport.akaNames) },
            {
                key: 'enquiry-history',
                title: 'Enquiry History',
                content: renderArrayTable('Enquiry History', activeXdsReport.consumerEnquiryHistory)
            },
            {
                key: 'directorship-links',
                title: 'Directorship Links',
                content: renderArrayTable('Directorship Links', activeXdsReport.consumerDirectorshipLinks)
            },
            {
                key: 'address-history',
                title: 'Address History',
                content: renderArrayTable('Address History', activeXdsReport.consumerAddressHistory)
            },
            {
                key: 'telephone-history',
                title: 'Telephone History',
                content: renderArrayTable('Telephone History', activeXdsReport.consumerTelephoneHistory)
            },
            {
                key: 'employment-history',
                title: 'Employment History',
                content: renderArrayTable('Employment History', activeXdsReport.consumerEmploymentHistory)
            },
            { key: 'email-history', title: 'Email History', content: renderArrayTable('Email History', activeXdsReport.consumerEmailHistory) }
        ].filter((section) => section.content !== null)
        : [];
    const scoringSections = activeScoringEngineResult
        ? [
            {
                key: 'scoring',
                title: 'Scoring',
                content: renderObjectTable('Scoring', {
                    ...toRecord(activeScoringEngineResult.scoring),
                    customer_id: undefined
                })
            },
            {
                key: 'policy',
                title: 'Policy',
                content: renderObjectTable('Policy', {
                    ...toRecord(activeScoringEngineResult.policy),
                    customer_id: undefined
                })
            }
        ].filter((section) => section.content !== null)
        : [];

    const activeEnquiryId = typeof activeXdsReport?.enquiryId === 'string'
        ? activeXdsReport.enquiryId
        : (typeof activeXdsReport?.enquiryId === 'number' ? String(activeXdsReport.enquiryId) : undefined);
    const activeEnquiryResultId = typeof activeXdsReport?.enquiryResultId === 'string'
        ? activeXdsReport.enquiryResultId
        : (typeof activeXdsReport?.enquiryResultId === 'number' ? String(activeXdsReport.enquiryResultId) : undefined);
    const canDownloadReport = downloadEnabled && Boolean(activeEnquiryId && activeEnquiryResultId);

    const handleDownloadReport = async () => {
        if (!activeEnquiryId || !activeEnquiryResultId) {
            toast.error('Missing enquiry identifiers for report download.');
            return;
        }

        setIsDownloading(true);
        try {
            const pdfBlob = await customAxiosInstanceMutator<Blob>({
                url: '/loan/api/v1/credit-report/download',
                method: 'GET',
                params: {
                    enquiryId: activeEnquiryId,
                    enquiryResultId: activeEnquiryResultId
                },
                responseType: 'blob'
            });

            const downloadUrl = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `xds-report-${activeEnquiryId}-${activeEnquiryResultId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            toast.error('Failed to download XDS report PDF.');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            centered
            scrollable
            fullscreen="lg-down"
            dialogClassName="director-reports-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fa fa-line-chart me-2"></i>
                    Director Reports
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="director-reports-modal__body">
                <div className="card border-0 shadow-sm mb-3 director-reports-modal__summary-card">
                    <div className="card-body py-3">
                        <div className="row g-3 align-items-center">
                            <div className="col-12 col-md-3">
                                <div className="small text-muted">Aggregate Score</div>
                                <div className="fw-bold fs-5">{aggregateScore ?? '-'}</div>
                            </div>
                            <div className="col-4 col-md-3">
                                <div className="small text-muted">Processed</div>
                                <div className="fw-semibold">{processedCount}</div>
                            </div>
                            <div className="col-4 col-md-3">
                                <div className="small text-muted">Successful</div>
                                <div className="fw-semibold text-success">{successCount}</div>
                            </div>
                            <div className="col-4 col-md-3">
                                <div className="small text-muted">Failed</div>
                                <div className="fw-semibold text-danger">{failedCount}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {reports.length === 0 && (
                    <div className="alert alert-info mb-0">
                        No director report data is available for this assessment.
                    </div>
                )}

                {reports.length > 0 && activeReport && (
                    <div className="director-reports-modal__content">
                        <div className="row g-3 mb-3">
                            <div className="col-12 col-md-6">
                                <label className="form-label fw-semibold mb-1">Select Director</label>
                                <select
                                    className="form-select"
                                    value={selectedIndex}
                                    onChange={(event) => onSelectIndex(Number(event.target.value))}
                                >
                                    {reports.map((report, index) => (
                                        <option key={report.id} value={index}>
                                            {report.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-12 col-md-3 d-flex align-items-end">
                                <div className="w-100">
                                    <label className="form-label fw-semibold mb-1">Status</label>
                                    <div>
                                        <span className={`badge ${getStatusBadgeClass(activeReport.status)}`}>
                                            {activeReport.status}
                                        </span>
                                        {typeof activeReport.creditScore === 'number' && (
                                            <span className="ms-2 small text-muted">
                                                Bureau Score: {activeReport.creditScore}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {showDownloadControl && (
                                <div className="col-12 col-md-3 d-flex align-items-end">
                                    <div className="w-100">
                                        <span
                                            className="d-inline-block w-100"
                                            title={!canDownloadReport ? downloadUnavailableMessage : undefined}
                                        >
                                            <Button
                                                variant="outline-primary"
                                                className="w-100"
                                                disabled={!canDownloadReport || isDownloading}
                                                style={!canDownloadReport ? { pointerEvents: 'none' } : undefined}
                                                onClick={handleDownloadReport}
                                            >
                                                {isDownloading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                        Downloading...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fa fa-download me-2"></i>
                                                        Download XDS Report
                                                    </>
                                                )}
                                            </Button>
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {activeReport.failureReason && (
                            <div className="alert alert-warning">
                                <strong>Director Report Warning:</strong> {activeReport.failureReason}
                            </div>
                        )}

                        <h6 className="fw-bold mt-4 mb-3">Scoring Engine Result</h6>
                        {activeScoringEngineResult ? (
                            <Accordion
                                alwaysOpen
                                defaultActiveKey={['scoring-scoring']}
                                className="director-reports-modal__accordion"
                            >
                                {scoringSections.map((section) => (
                                    <Accordion.Item eventKey={`scoring-${section.key}`} key={`scoring-${section.key}`}>
                                        <Accordion.Header>{section.title}</Accordion.Header>
                                        <Accordion.Body>{section.content}</Accordion.Body>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        ) : (
                            <div className="alert alert-secondary mb-0">No scoring engine result payload available for this director.</div>
                        )}

                        <h6 className="fw-bold mt-4 mb-3">XDS Report</h6>
                        {activeXdsReport ? (
                            <Accordion
                                alwaysOpen
                                defaultActiveKey={['xds-consumer-cpanlr-debt-summary', 'xds-consumer-account-status']}
                                className="director-reports-modal__accordion mb-3"
                            >
                                {xdsSections.map((section) => (
                                    <Accordion.Item eventKey={`xds-${section.key}`} key={`xds-${section.key}`}>
                                        <Accordion.Header>{section.title}</Accordion.Header>
                                        <Accordion.Body>{section.content}</Accordion.Body>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        ) : (
                            <div className="alert alert-secondary">No XDS report payload available for this director.</div>
                        )}

                        <h6 className="fw-bold mt-4 mb-3">Attributes</h6>
                        {activeAttributes ? (
                            renderObjectTable('Attributes', activeAttributes)
                        ) : (
                            <div className="alert alert-secondary">No attributes payload available for this director.</div>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreditDirectorReportsModal;
